import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAuthStore } from '@store/authStore';

// Pages
import LandingPage from '@pages/LandingPage';
import PricingPage from '@pages/PricingPage';
import TeamPage from '@pages/TeamPage';
import PrivacyPolicyPage from '@pages/PrivacyPolicyPage';
import SportPage from '@pages/SportPage';
import SponsorLandingPage from '@pages/SponsorLandingPage';
import SponsorOpportunitiesPage from '@pages/SponsorOpportunitiesPage';
import SponsorDashboardPage from '@pages/SponsorDashboardPage';
import PublicAcademyProfilePage from '@pages/PublicAcademyProfilePage';
import PublicProfileDispatchPage from '@pages/PublicProfileDispatchPage';
import LoginPage from '@pages/auth/LoginPage';
import RegisterPlayerPage from '@pages/auth/RegisterPlayerPage';
import RegisterCoachPage from '@pages/auth/RegisterCoachPage';
import RegisterSponsorPage from '@pages/auth/RegisterSponsorPage';
import AcademyRegistrationPage from '@pages/auth/AcademyRegistrationPage';
import ForgotPasswordPage from '@pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@pages/auth/ResetPasswordPage';
import VerifyEmailPage from '@pages/auth/VerifyEmailPage';
import FamilyOnboardingPage from '@pages/auth/FamilyOnboardingPage';
import VerifyParentalConsentPage from '@pages/auth/VerifyParentalConsentPage';
import ClaimPlayerEmailPage from '@pages/auth/ClaimPlayerEmailPage';
import DashboardPage from '@pages/DashboardPage';
import AdminDashboard from '@pages/admin/AdminDashboard';
import AdminRankingStrokeTypesManagement from '@pages/admin/AdminRankingStrokeTypesManagement';
import CoachDashboard from '@pages/CoachDashboard';
import PlayerDashboard from '@pages/PlayerDashboard';
import PlayerPerformancePage from '@pages/PlayerPerformancePage';
import PlayerRankingEvaluationPage from '@pages/PlayerRankingEvaluationPage';
import PlayerRankingOverviewPage from '@pages/PlayerRankingOverviewPage';
import ContestOverviewPage from '@pages/ContestOverviewPage';
import ContestSportPage from '@pages/ContestSportPage';
import PublicContestPage from '@pages/PublicContestPage';
import ContestSelfEvaluationPage from '@pages/ContestSelfEvaluationPage';
import ContestRankingLegacyRedirect from '@pages/ContestRankingLegacyRedirect';
import MotorPsychomotorPage from '@pages/MotorPsychomotorPage';
import FitnessStandardsPage from '@pages/FitnessStandardsPage';
import BlogsPage from '@pages/BlogsPage';
import PartnersPage from '@pages/PartnersPage';
import SubscriptionsPage from '@pages/SubscriptionsPage';
import SubscriptionContactPage from '@pages/SubscriptionContactPage';
import SubscriptionPurchasePage from '@pages/SubscriptionPurchasePage';
import SupportPage from '@pages/SupportPage';
import CareersPage from '@pages/CareersPage';
import PlayerRankingReportViewPage from '@pages/PlayerRankingReportViewPage';
import MyReportsPage from '@pages/MyReportsPage';
import PlayerRecommendationsPage from '@pages/PlayerRecommendationsPage';
import ComparePeersPage from '@pages/ComparePeersPage';
import PlayerDrillsPage from '@pages/PlayerDrillsPage';
import PlayerFitnessPage from '@pages/PlayerFitnessPage';
import CoachProfile from '@pages/CoachProfile';
import AcademyManagement from '@pages/AcademyManagement';
import CoachCertificatesPage from '@pages/CoachCertificatesPage';
import CoachPlayerTrainingPage from '@pages/CoachPlayerTrainingPage';
import CoachPlayerRankingPage from '@pages/CoachPlayerRankingPage';
import CoachCompareStudentsPage from '@pages/CoachCompareStudentsPage';
import CoachMyRankingsLegacyRedirect from '@pages/CoachMyRankingsLegacyRedirect';
import CoachMyTrainingsLegacyRedirect from '@pages/CoachMyTrainingsLegacyRedirect';
import CoachSelfPerformanceLegacyRedirect from '@pages/CoachSelfPerformanceLegacyRedirect';
import CoachStudentPerformanceLegacyRedirect from '@pages/CoachStudentPerformanceLegacyRedirect';
import CoachLayout from '@components/layout/CoachLayout';
import JuniorCoachManagement from '@pages/JuniorCoachManagement';
import AdminAcademyManagement from '@pages/admin/AdminAcademyManagement';
import AcademyAdminDashboard from '@pages/AcademyAdminDashboard';
import BillingInvoicesPage from '@pages/BillingInvoicesPage';
import PaymentGatewayPage from '@pages/PaymentGatewayPage';
import LearningMainPage from '@pages/LearningMainPage';
import SportLearningPage from '@pages/SportLearningPage';
// import CoachManagementDashboard from '@components/CoachManagementDashboard';
// import CoachPermissionsPage from '@components/CoachPermissionsPage';
import NotFoundPage from '@pages/NotFoundPage';

// Components
import PrivateRoute from '@components/PrivateRoute';
import PublicRoute from '@components/PublicRoute';
import FeatureRoute from '@components/FeatureRoute';
import ContestLeaderboardPage from '@pages/ContestLeaderboardPage';

function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    // Check authentication status on app load
    checkAuth();
  }, [checkAuth]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register/player"
          element={
            <PublicRoute>
              <RegisterPlayerPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register/coach"
          element={
            <PublicRoute>
              <RegisterCoachPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register/academy"
          element={
            <PublicRoute>
              <AcademyRegistrationPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register/sponsor"
          element={
            <PublicRoute>
              <FeatureRoute feature="registerSponsor" redirectTo="/partners">
              <RegisterSponsorPage />
            </FeatureRoute>
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-email"
          element={<VerifyEmailPage />}
        />
        <Route
          path="/family-setup"
          element={
            <PrivateRoute>
              <FamilyOnboardingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/verify-parental-consent"
          element={<VerifyParentalConsentPage />}
        />
        <Route
          path="/claim-player-email"
          element={<ClaimPlayerEmailPage />}
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/sponsor/dashboard"
          element={
            <PrivateRoute>
              <SponsorDashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/:sport/master-drills"
          element={
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/:sport/master-fitness"
          element={
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/academy-management"
          element={
            <PrivateRoute>
              <AdminAcademyManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ranking-stroke-types"
          element={
            <PrivateRoute>
              <AdminRankingStrokeTypesManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach"
          element={
            <PrivateRoute>
              <CoachLayout>
                <CoachDashboard />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/dashboard"
          element={
            <PrivateRoute>
              <CoachLayout>
                <CoachDashboard />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/player"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerDashboard">
              <PlayerDashboard />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/dashboard"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerDashboard">
              <PlayerDashboard />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/profile"
          element={
            <PrivateRoute>
              <PlayerDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/player/invoices"
          element={
            <PrivateRoute>
              <PlayerDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/player/billing/:gateway"
          element={
            <PrivateRoute>
              <PaymentGatewayPage />
            </PrivateRoute>
          }
        />
        <Route path="/player/family" element={<Navigate to="/player/profile" replace />} />
        <Route path="/player/children" element={<Navigate to="/player/profile?tab=children" replace />} />
        <Route
          path="/player/certificates"
          element={
            <PrivateRoute>
              <PlayerDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        {/* Sport-based routes */}
        <Route
          path="/player/performance/:sport/:assessmentType"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/skipping"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/running"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/wall-situp"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/service"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/drop-shot"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/smash"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/clear"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/net-play"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/drill-eval"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/ranking-self"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/ranking-coach"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/performance/ranking-practical"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerTrainingEval">
              <PlayerPerformancePage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/ranking-overview"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerRanking">
              <PlayerRankingOverviewPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/ranking-evaluation/contest"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerRanking">
              <ContestRankingLegacyRedirect />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/ranking-evaluation/:subcategory/:evalType"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerRanking">
              <PlayerRankingEvaluationPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/ranking-evaluation/:categoryId"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerRanking">
              <PlayerRankingEvaluationPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/contests"
          element={
            <PrivateRoute>
              <ContestOverviewPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/player/contests/:sport"
          element={
            <PrivateRoute>
              <Navigate to="all" replace />
            </PrivateRoute>
          }
        />
        <Route
          path="/player/contests/:sport/:category"
          element={
            <PrivateRoute>
              <ContestSportPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/player/ranking-report/:reportId"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerRanking">
              <PlayerRankingReportViewPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/my-reports"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerReports">
              <MyReportsPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/recommendations"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerAiAssistant">
              <PlayerRecommendationsPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/compare-peers"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerPeerBenchmark">
              <ComparePeersPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/drills"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerDrillsFitness">
              <PlayerDrillsPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/fitness"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerDrillsFitness">
              <PlayerFitnessPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/learning"
          element={
            <FeatureRoute feature="playerLearning">
              <Navigate to="/player/learning/courses" replace />
            </FeatureRoute>
          }
        />
        <Route
          path="/player/learning/courses"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerLearning">
              <LearningMainPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/learning/roadmap"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerLearning">
              <LearningMainPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/learning/courses/:sport"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerLearning">
              <SportLearningPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/learning/:sport"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerLearning">
              <SportLearningPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/learning/courses/:sport/skill/:skillId"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerLearning">
              <SportLearningPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/player/learning/:sport/skill/:skillId"
          element={
            <PrivateRoute>
              <FeatureRoute feature="playerLearning">
              <SportLearningPage />
            </FeatureRoute>
            </PrivateRoute>
          }
        />
        {/* Legacy coach-as-player routes → unified Training Evaluation */}
        <Route
          path="/coach/self-evaluation"
          element={
            <PrivateRoute>
              <CoachSelfPerformanceLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/self-evaluation/:metric"
          element={
            <PrivateRoute>
              <CoachSelfPerformanceLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/player-performance"
          element={
            <PrivateRoute>
              <CoachSelfPerformanceLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/player-performance/:metric"
          element={
            <PrivateRoute>
              <CoachSelfPerformanceLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/profile"
          element={
            <PrivateRoute>
              <CoachLayout>
                <CoachProfile />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/invoices"
          element={
            <PrivateRoute>
              <CoachLayout>
                <BillingInvoicesPage />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/billing/:gateway"
          element={
            <PrivateRoute>
              <PaymentGatewayPage />
            </PrivateRoute>
          }
        />
        {/* Academy Admin Routes */}
        <Route
          path="/academy-admin/dashboard"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/coaches"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/players"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/admins"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/profile"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/invoices"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/billing/:gateway"
          element={
            <PrivateRoute>
              <PaymentGatewayPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/reports/player-wise"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/reports/sport-wise"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/reports"
          element={
            <PrivateRoute>
              <Navigate to="/academy-admin/reports/player-wise" replace />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/attendance"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/academy-admin/attendance/report"
          element={
            <PrivateRoute>
              <AcademyAdminDashboard />
            </PrivateRoute>
          }
        />
        {/* Academy Management Routes */}
        <Route
          path="/coach/academy-management"
          element={
            <PrivateRoute>
              <CoachLayout>
                <AcademyManagement />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/academy-management/academies"
          element={
            <PrivateRoute>
              <CoachLayout>
                <AcademyManagement />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/academy-management/players"
          element={
            <PrivateRoute>
              <CoachLayout>
                <AcademyManagement />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/academy-management/coaches"
          element={
            <PrivateRoute>
              <CoachLayout>
                <AcademyManagement />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/academy-management/permissions"
          element={
            <PrivateRoute>
              <CoachLayout>
                <AcademyManagement />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        {/* Learn and Rank — coach-as-player (parity with player portal) */}
        <Route
          path="/coach/ranking-overview"
          element={
            <PrivateRoute>
              <CoachLayout>
                <PlayerRankingOverviewPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/ranking-evaluation/contest"
          element={
            <PrivateRoute>
              <ContestRankingLegacyRedirect portalMode="coach" />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/ranking-evaluation/:subcategory/:evalType"
          element={
            <PrivateRoute>
              <PlayerRankingEvaluationPage portalMode="coach" />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/ranking-evaluation/:categoryId"
          element={
            <PrivateRoute>
              <PlayerRankingEvaluationPage portalMode="coach" />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/contests"
          element={
            <PrivateRoute>
              <CoachLayout>
                <ContestOverviewPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/contests/:sport"
          element={
            <PrivateRoute>
              <Navigate to="all" replace />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/contests/:sport/:category"
          element={
            <PrivateRoute>
              <ContestSportPage portalMode="coach" />
            </PrivateRoute>
          }
        />
        {/* Training Evaluation — coach-as-player (parity with player portal) */}
        <Route
          path="/coach/performance"
          element={
            <PrivateRoute>
              <PlayerPerformancePage portalMode="coach" />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/performance/:sport/:assessmentType"
          element={
            <PrivateRoute>
              <PlayerPerformancePage portalMode="coach" />
            </PrivateRoute>
          }
        />
        {/* Legacy My Trainings routes → redirect to unified performance flow */}
        <Route
          path="/coach/my-trainings"
          element={
            <PrivateRoute>
              <Navigate to="/coach/performance/Badminton/skill-assessment-beginner-self" replace />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/my-trainings/:sport/:assessmentType"
          element={
            <PrivateRoute>
              <CoachMyTrainingsLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/my-rankings"
          element={<Navigate to="/coach/ranking-overview" replace />}
        />
        <Route
          path="/coach/my-rankings/:sport/:categoryId"
          element={
            <PrivateRoute>
              <CoachMyRankingsLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/compare-students"
          element={
            <PrivateRoute>
              <CoachLayout>
                <CoachCompareStudentsPage />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/reports"
          element={
            <PrivateRoute>
              <CoachLayout>
                <MyReportsPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/recommendations"
          element={
            <PrivateRoute>
              <CoachLayout>
                <PlayerRecommendationsPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/ranking-report/:reportId"
          element={
            <PrivateRoute>
              <CoachLayout>
                <PlayerRankingReportViewPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/learning"
          element={<Navigate to="/coach/learning/courses" replace />}
        />
        <Route
          path="/coach/learning/courses"
          element={
            <PrivateRoute>
              <CoachLayout>
                <LearningMainPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/learning/roadmap"
          element={
            <PrivateRoute>
              <CoachLayout>
                <LearningMainPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/learning/courses/:sport"
          element={
            <PrivateRoute>
              <CoachLayout>
                <SportLearningPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/learning/:sport"
          element={
            <PrivateRoute>
              <CoachLayout>
                <SportLearningPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/learning/courses/:sport/skill/:skillId"
          element={
            <PrivateRoute>
              <CoachLayout>
                <SportLearningPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/learning/:sport/skill/:skillId"
          element={
            <PrivateRoute>
              <CoachLayout>
                <SportLearningPage portalMode="coach" />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        {/* My Certificates Route */}
        <Route
          path="/coach/certificates"
          element={
            <PrivateRoute>
              <CoachLayout>
                <CoachCertificatesPage />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        {/* Player Training Routes */}
        <Route
          path="/coach/player-training"
          element={
            <PrivateRoute>
              <CoachPlayerTrainingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/player-training/:sport/:assessmentType"
          element={
            <PrivateRoute>
              <CoachPlayerTrainingPage />
            </PrivateRoute>
          }
        />
        {/* Player Ranking Routes */}
        <Route
          path="/coach/player-ranking"
          element={
            <PrivateRoute>
              <CoachPlayerRankingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/player-ranking/:sport/:categoryId"
          element={
            <PrivateRoute>
              <CoachPlayerRankingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/junior-coaches"
          element={
            <PrivateRoute>
              <CoachLayout>
                <JuniorCoachManagement />
              </CoachLayout>
            </PrivateRoute>
          }
        />
        {/* Legacy student performance routes → Player Training / Player Ranking */}
        <Route
          path="/coach/player/:playerId/performance"
          element={
            <PrivateRoute>
              <CoachStudentPerformanceLegacyRedirect />
            </PrivateRoute>
          }
        />
        <Route
          path="/coach/player/:playerId/performance/:metric"
          element={
            <PrivateRoute>
              <CoachStudentPerformanceLegacyRedirect />
            </PrivateRoute>
          }
        />

        {/* Landing page */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<FeatureRoute feature="publicPricing" redirectTo="/"><PricingPage /></FeatureRoute>} />
        <Route path="/team" element={<FeatureRoute feature="publicTeam" redirectTo="/"><TeamPage /></FeatureRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<PrivacyPolicyPage />} />
        <Route path="/sponsors" element={<FeatureRoute feature="publicSponsors" redirectTo="/"><SponsorLandingPage /></FeatureRoute>} />
        <Route path="/sponsors/opportunities" element={<FeatureRoute feature="publicSponsors" redirectTo="/"><SponsorOpportunitiesPage /></FeatureRoute>} />
        
        {/* Public contest catalog (EncycGames 1.0) */}
        <Route path="/contests/:sport/leaderboard" element={<ContestLeaderboardPage />} />
        <Route path="/contests/:sport/self-evaluation" element={<ContestSelfEvaluationPage />} />
        <Route path="/contests/:sport" element={<PublicContestPage />} />
        <Route path="/motor-psychomotor" element={<MotorPsychomotorPage />} />
        <Route path="/fitness-standards" element={<FitnessStandardsPage />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/blogs/:slug" element={<BlogsPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions/purchase" element={<SubscriptionPurchasePage />} />
        <Route path="/subscriptions/contact" element={<SubscriptionContactPage />} />
        <Route path="/subscriptions/corporate" element={<Navigate to="/subscriptions" replace />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/careers" element={<CareersPage />} />

        {/* Sport pages */}
        <Route path="/sport/:sportName" element={<FeatureRoute feature="publicSportHub" redirectTo="/"><SportPage /></FeatureRoute>} />
        <Route path="/academy/:academyCode" element={<PublicAcademyProfilePage />} />
        <Route path="/in/:slug" element={<PublicProfileDispatchPage />} />
        
        {/* Redirects */}
        <Route path="/dashboard" element={<Navigate to="/login" replace />} />
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Box>
  );
}

export default App;


